import { getClientTraceHeaders } from "./clientTraceHeaders";
import { computePlatformHint } from "./platformHint";

type LogClientPayload = {
  action: string;
  description: string;
  endpoint?: string;
  method?: string;
  userId?: number | null;
  additionalData?: Record<string, unknown>;
};

/** Не-PII снимок клиента для корреляции «медленно у части пользователей». */
function getClientEnvSnapshot(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
      deviceMemory?: number;
    };
    const c = nav.connection;
    let wwInitToNowMs: number | null = null;
    try {
      const t = Number(sessionStorage.getItem("ww_init_ts") || "0");
      wwInitToNowMs = t > 0 ? Date.now() - t : null;
    } catch {
      wwInitToNowMs = null;
    }
    let tgMiniAppContext = false;
    try {
      const h = window.location.hash || "";
      const s = window.location.search || "";
      tgMiniAppContext = h.includes("tgWebAppData") || s.includes("tgWebApp");
    } catch {
      tgMiniAppContext = false;
    }
    return {
      platformHint: computePlatformHint(),
      pwaStandalone: (() => {
        try {
          return (
            window.matchMedia("(display-mode: standalone)").matches ||
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true
          );
        } catch {
          return false;
        }
      })(),
      tgMiniAppContext,
      connEffectiveType: c?.effectiveType ?? null,
      connDownlinkMbps: typeof c?.downlink === "number" ? c.downlink : null,
      connRttMs: typeof c?.rtt === "number" ? c.rtt : null,
      saveData: Boolean(c?.saveData),
      deviceMemoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
      hardwareConcurrency: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
      viewportCss: `${window.innerWidth}x${window.innerHeight}`,
      dpr: typeof window.devicePixelRatio === "number" ? window.devicePixelRatio : null,
      wwInitToNowMs,
    };
  } catch {
    return {};
  }
}

/**
 * Отправка диагностики на backend (POST /api/error-log).
 * Не бросает наружу — сбой логирования не должен ломать UX.
 */
/** Антиспам: одинаковые ошибки медиа не чаще раз в N мс. */
const lessonMediaErrorLogAt = new Map<string, number>();
const LESSON_MEDIA_ERR_COOLDOWN_MS = 25_000;

/**
 * Ошибка загрузки аудио/видео/картинки в уроке → POST /api/error-log (через logClientDiagnostic).
 * Отдельного эндпоинта «дозагрузки» нет: клиент только перезапрашивает URL с cache-bust.
 */
export function logLessonMediaLoadFailure(params: {
  kind: "audio" | "video" | "image";
  src: string;
  exerciseId?: number;
  mediaErrorCode?: number | null;
  mediaErrorMessage?: string | null;
}): void {
  const srcKey = String(params.src || "").slice(0, 800);
  const dedupKey = `${params.kind}|${params.exerciseId ?? ""}|${srcKey}`;
  const now = Date.now();
  const prev = lessonMediaErrorLogAt.get(dedupKey) ?? 0;
  if (now - prev < LESSON_MEDIA_ERR_COOLDOWN_MS) return;
  lessonMediaErrorLogAt.set(dedupKey, now);

  logClientDiagnostic({
    action: "lesson.media_load_failed",
    description: `Lesson ${params.kind} load error: ${srcKey.slice(0, 160)}`,
    endpoint: "/lesson/media",
    method: "MEDIA",
    additionalData: {
      mediaKind: params.kind,
      src: srcKey,
      exerciseId: params.exerciseId ?? null,
      mediaErrorCode: params.mediaErrorCode ?? null,
      mediaErrorMessage: params.mediaErrorMessage ?? null,
    },
  });
}

export function logClientDiagnostic(payload: LogClientPayload): void {
  try {
    const body = {
      error: {
        name: "ClientDiagnostic",
        message: payload.description,
        stack: "",
        code: null,
      },
      endpoint: payload.endpoint || "/client/diagnostic",
      method: payload.method || "CLIENT",
      userInfo: {
        type: "user",
        id: payload.userId ?? null,
        tg_id: null,
        email: null,
        username: null,
      },
      context: {
        source: "client",
        action: payload.action,
        description: payload.description,
      },
      request: {
        body: {},
        query: {},
        params: {},
        headers: {
          ...getClientTraceHeaders(),
        },
      },
      response: { status: 0, data: null },
      additionalData: {
        href: typeof window !== "undefined" ? window.location.href : null,
        ...getClientEnvSnapshot(),
        ...payload.additionalData,
      },
    };

    void fetch("/api/error-log", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
        ...getClientTraceHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    // ignore
  }
}
